import { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useLanguage } from '@/i18n/LanguageContext';
import { useSignedUrls } from '@/hooks/useSignedUrl';
import { useScrollReveal } from '@/hooks/useScrollReveal';

const AtelierTeaser = () => {
  const { t } = useLanguage();
  const { ref, isVisible } = useScrollReveal();

  const { data: images = [] } = useQuery({
    queryKey: ['atelier-teaser-images'],
    queryFn: async () => {
      const { data } = await supabase
        .from('gallery_images')
        .select('*')
        .eq('context_area', 'homepage')
        .eq('role', 'editorial')
        .eq('is_visible', true)
        .order('display_order')
        .limit(1);
      return data || [];
    },
  });

  const paths = useMemo(() => images.map(i => i.storage_path), [images]);
  const urls = useSignedUrls(paths);

  if (images.length === 0) return null;

  const image = images[0];
  const imageUrl = urls.get(image.storage_path);

  return (
    <section className="section-padding" aria-label={t.atelierTeaserTitle}>
      <div ref={ref} className="max-w-6xl mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">
          {/* Image */}
          <div className={`reveal-on-scroll ${isVisible ? 'revealed' : ''}`}>
            <div className="aspect-[4/3] overflow-hidden bg-secondary/50 img-frame">
              {imageUrl && (
                <img
                  src={imageUrl}
                  alt={image.alt_text || t.atelierTeaserTitle}
                  className="w-full h-full object-cover"
                  loading="lazy"
                />
              )}
            </div>
          </div>

          {/* Text */}
          <div className={`reveal-on-scroll reveal-on-scroll-delay-1 ${isVisible ? 'revealed' : ''}`}>
            <div className="w-12 h-px bg-gold mb-8" aria-hidden="true" />
            <h2 className="font-serif text-3xl sm:text-4xl text-foreground mb-6">
              {t.atelierTeaserTitle}
            </h2>
            <p className="text-muted-foreground leading-relaxed mb-8">
              {t.atelierTeaserText}
            </p>
            <Link
              to="/atelier"
              className="inline-block px-8 py-3 border border-foreground/20 text-xs tracking-[0.2em] uppercase font-sans text-foreground hover:bg-foreground hover:text-background transition-all duration-300 rounded-full"
            >
              {t.atelierTeaserCta}
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
};

export default AtelierTeaser;
